import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { graphql, Link } from 'gatsby';
import Img from 'gatsby-image';

import Layout from '../components/layout';
import SEO from '../components/seo';
import Heading2 from '../ui/Heading2';
import FAQAccordion from '../components/FAQAccordion';
import { BREAKPOINTS, COLORS } from '../ui/variables';
import Heading3 from '../ui/Heading3';
import { ReactComponent as ArrowRightIco } from '../images/icons/arrow-right.svg';
import Button from '../ui/BaseButton';
import Heading1 from '../ui/Heading1';
import ParagraphExLarge from '../ui/ParagraphExLarge';

import { ServicePostBySlugQuery } from '../../graphql-types';
import renderRichBody from '../components/article/renderArticleRichText';
import UppercaseOrange from '../ui/UppercaseOrange';
import { Uppercase } from '../ui/TypographyStyles';

const ArticleContainer = styled.article`
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FAQ = styled.div`
  max-width: 810px;
  width: 100%;
  margin-bottom: 120px;

  @media ${BREAKPOINTS.TABLET} {
    max-width: 615px;
  }
`;

const Related = styled.div`
  background: ${COLORS.BLUE};
  padding: 120px 0;

  ul {
    max-width: 1242px;
    margin: auto;
    list-style: none;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    overflow-x: scroll;

    @media ${BREAKPOINTS.TABLET} {
      padding: 0 40px;
    }
  }
`;

const RelatedPost = styled.li`
  max-width: 378px;
  min-width: 287px;
  flex-shrink: 0;
  background: ${COLORS.WHITE};
  border-radius: 10px;
  padding: 40px;
  margin: 0 27px;

  @media ${BREAKPOINTS.TABLET} {
    max-width: 287px;
    margin: 0 12px;
  }

  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    margin-right: 0;
  }
`;

const ArticleHeader = styled.header`
  max-width: 1280px;
  width: 100%;
  display: flex;
  margin-bottom: 120px;
  margin-top: 20px;

  @media ${BREAKPOINTS.TABLET} {
    padding-left: 10px;
    padding-right: 10px;
    margin-bottom: 100px;
  }

  @media screen and (max-width: 700px) {
    width: 100%;
    flex-direction: column;
    padding-left: 0;
    padding-right: 0;
    padding-top: 60px;
  }
`;

const HeaderLeft = styled.div`
  max-width: 600px;
  align-self: center;

  @media ${BREAKPOINTS.TABLET} {
    max-width: 482px;
  }
`;

const HeaderRight = styled.div`
  padding-left: 100px;
  flex-grow: 1;
  min-width: 300px;
  margin-left: 26px;

  @media screen and (max-width: 1130px) {
    padding-left: 30px;
  }

  @media ${BREAKPOINTS.TABLET} {
    padding-left: 0;
  }

  @media screen and (max-width: 700px) {
    padding-left: 0;
    margin-top: 80px;
    margin-left: 0px;
  }
`;

const HeaderImageWrap = styled.div`
  padding: 60px 54px 0 54px;
  position: relative;

  @media ${BREAKPOINTS.TABLET} {
    padding: 60px 41px 0 41px;
  }

  &::before {
    content: ' ';
    width: 100%;
    display: block;
    position: absolute;
    background: ${COLORS.BEIGE};
    top: 0;
    left: 0;
    height: calc(100% - 36px);
  }
`;

const JoinCTA = styled(Button)`
  padding: 16px 32px;
`;

export const pageQuery = graphql`
  query ServicePostBySlug($slug: String!) {
    contentfulService(urlSlug: { eq: $slug }) {
      title
      urlSlug
      pageTitle
      phoneCTA
      description {
        description
      }
      heroImage {
        fluid(maxWidth: 450) {
          ...GatsbyContentfulFluid_withWebp
        }
        title
      }
      body {
        raw
        references {
          ... on ContentfulArticleSingleImage {
            __typename
            contentful_id
            image {
              title
              fluid(maxWidth: 1027) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
          }
        }
      }
      faqGroup {
        FAQItems {
          ... on ContentfulFaqItem {
            id
            answer {
              answer
            }
            question
          }
        }
      }
      relatedServices {
        title
        urlSlug
        description {
          description
        }
      }
      schemaMarkup {
        _context
        _type
        mainEntity {
          _type
          name
          acceptedAnswer {
            _type
            text
          }
        }
      }
      breadcrumbSchema {
        _context
        _type
        itemListElement {
          _type
          position
          name
          item
        }
      }
    }
  }
`;

interface IServicePostProps {
  data: ServicePostBySlugQuery;
}

const ServicePost = ({ data }: IServicePostProps) => {
  const service = data.contentfulService;

  if (!service) {
    return null;
  }

  const FAQItems = service.faqGroup.FAQItems.map(item => ({
    question: item.question,
    answer: item.answer.answer,
  }));

  return (
    <Layout>
      <SEO
        title={service.pageTitle || service.title}
        description={service.description.description}
        image={service.heroImage.fluid.src}
        schema={service.schemaMarkup}
        breadcrumbSchema={service.breadcrumbSchema}
      />
      <ArticleContainer>
        <ArticleHeader>
          <HeaderLeft>
            <Uppercase
              css={css`
                color: ${COLORS.ORANGE};
                margin-bottom: 24px;
                display: inline-block;
              `}
            >
              Services
            </Uppercase>
            <Heading1
              css={css`
                max-width: 740px;
              `}
            >
              {service.title}
            </Heading1>
            <ParagraphExLarge>
              {service.description.description}
            </ParagraphExLarge>
            {!service.phoneCTA && (
              <JoinCTA
                type='external'
                href={`${process.env.GATSBY_NEW_ONBOARDING_URL}signup`}
                color={COLORS.ORANGE}
              >
                Book Now
              </JoinCTA>
            )}
            {service.phoneCTA && (
              <JoinCTA
                href='tel:(212) 933-9044'
                type='external'
                color={COLORS.ORANGE}
              >
                Call (212) 933-9044
              </JoinCTA>
            )}
          </HeaderLeft>

          <HeaderRight>
            <HeaderImageWrap>
              <Img
                css={css`
                  max-width: 432px;
                `}
                alt={service.heroImage.title}
                fluid={service.heroImage.fluid}
              />
            </HeaderImageWrap>
          </HeaderRight>
        </ArticleHeader>

        {service.body &&
          service.body.raw &&
          renderRichBody({
            raw: service.body.raw,
            references: service.body.references,
          })}

        <FAQ>
          <Heading2>Common questions</Heading2>
          <FAQAccordion items={FAQItems} />
        </FAQ>
      </ArticleContainer>

      {service.relatedServices && service.relatedServices.length > 0 && (
        <Related>
          <Heading2
            css={css`
              text-align: center;
              margin-bottom: 60px;
            `}
          >
            Related services
          </Heading2>
          <ul>
            {service.relatedServices.map(service => (
              <RelatedPost key={service.urlSlug}>
                <Heading3
                  css={css`
                    margin-bottom: 16px;
                  `}
                >
                  {service.title}
                </Heading3>
                {service.description.description}
                <Link
                  css={css`
                    margin-top: 24px;
                    display: block;
                  `}
                  to={`/services/${service.urlSlug}`}
                >
                  Learn more{' '}
                  <ArrowRightIco
                    width='14'
                    height='11'
                    css={css`
                      fill: ${COLORS.ORANGE};
                    `}
                  />
                </Link>
              </RelatedPost>
            ))}
            <li
              css={css`
                min-width: 20px;
                height: 1px;
              `}
            />
          </ul>
        </Related>
      )}
    </Layout>
  );
};

export default ServicePost;
